import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectTaskAssignmentRole,
  assignProjectTask,
  listProjectMembers,
  listProjectTaskAssignments,
  projectTaskAssignmentRoleNames,
  unassignProjectTask,
  updateProjectTaskAssignment,
} from '../../api/projects';
import type {
  ProjectMember,
  ProjectTaskAssignmentItem,
  ProjectTaskAssignmentRoleValue,
  ProjectTaskItem,
} from '../../api/projects';
import { listUserAccounts } from '../../api/userAccounts';
import type { UserAccount } from '../../api/userAccounts';
import { formatDate } from './projectDisplay';

/** Giá trị của biểu mẫu giao việc. */
interface AssignFormValues {
  readonly userAccountId: string;
  readonly role: ProjectTaskAssignmentRoleValue;
  readonly allocationPercent: number | null;
  readonly plannedEffortHours: number | null;
}

/** Số bản ghi nạp cho ô chọn người. */
const lookupPageSize = 200;

/** Các lựa chọn vai trò trong công việc. */
const roleOptions = Object.entries(projectTaskAssignmentRoleNames).map(([value, label]) => ({
  value: Number(value) as ProjectTaskAssignmentRoleValue,
  label,
}));

/**
 * Hộp thoại giao việc cho người.
 *
 * <b>Ô chọn người chỉ hiện thành viên đang tham gia dự án.</b> Máy chủ cũng chặn, nhưng hiện cả
 * những người khác rồi để máy chủ từ chối là cách chắc chắn làm người dùng bực: họ chọn một cái tên
 * có sẵn và nhận về một thông báo nói người đó không được. Lý do đằng sau phép chặn ấy là thật —
 * giao việc cho người ngoài dự án sẽ tạo ra một công việc mà chính họ không mở ra được.
 *
 * <b>Cột xác nhận nhận việc cho người giao biết việc đã tới tay ai.</b> Một việc giao ba ngày chưa
 * ai xác nhận là dấu hiệu cần hỏi, không phải dấu hiệu người đó đang làm.
 */
export function ProjectTaskAssignmentModal({
  projectId,
  task,
  canManage,
  open,
  onClose,
}: {
  readonly projectId: string;
  readonly task: ProjectTaskItem | null;
  readonly canManage: boolean;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [form] = Form.useForm<AssignFormValues>();

  const [assignments, setAssignments] =
    useState<readonly ProjectTaskAssignmentItem[] | null>(null);
  const [members, setMembers] = useState<readonly ProjectMember[]>([]);
  const [users, setUsers] = useState<readonly UserAccount[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!open || task === null) {
      return;
    }

    let isCurrent = true;

    async function load(taskId: string): Promise<void> {
      setErrorMessage(null);

      try {
        const [loadedAssignments, loadedMembers] = await Promise.all([
          listProjectTaskAssignments(projectId, taskId),
          listProjectMembers(projectId),
        ]);

        /*
          Tên người là thứ CÓ CŨNG ĐƯỢC: danh mục tài khoản đòi một quyền khác với quyền xem công
          việc, nên người quản lý dự án không có quyền đó vẫn phải dùng được hộp thoại này.
        */
        let loadedUsers: readonly UserAccount[] = [];

        try {
          const page = await listUserAccounts({ pageSize: lookupPageSize });

          loadedUsers = page.items;
        } catch {
          loadedUsers = [];
        }

        if (!isCurrent) {
          return;
        }

        setAssignments(loadedAssignments);
        setMembers(loadedMembers);
        setUsers(loadedUsers);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách phân công.',
        );
      }
    }

    void load(task.id);

    return () => {
      isCurrent = false;
    };
  }, [projectId, task, open, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  async function submit(values: AssignFormValues): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      await assignProjectTask(
        projectId,
        task.id,
        values.userAccountId,
        values.role,
        values.allocationPercent ?? null,
        values.plannedEffortHours ?? null,
      );

      message.success('Đã giao việc.');
      form.resetFields();
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không giao được việc.');
    } finally {
      setIsSaving(false);
    }
  }

  async function changeRole(
    assignment: ProjectTaskAssignmentItem,
    role: ProjectTaskAssignmentRoleValue,
  ): Promise<void> {
    if (task === null) {
      return;
    }

    try {
      await updateProjectTaskAssignment(
        projectId,
        task.id,
        assignment.id,
        role,
        assignment.allocationPercent,
        assignment.plannedEffortHours,
      );

      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được vai trò.');
    }
  }

  async function remove(assignmentId: string): Promise<void> {
    if (task === null) {
      return;
    }

    try {
      await unassignProjectTask(projectId, task.id, assignmentId);

      message.success('Đã gỡ khỏi công việc.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không gỡ được.');
    }
  }

  /** Tên hiển thị của một người, hoặc chính định danh khi không tra được. */
  function displayName(userAccountId: string): string {
    const user = users.find((candidate) => candidate.id === userAccountId);

    return user === undefined ? userAccountId : `${user.fullName} (${user.userName})`;
  }

  const columns: TableColumnsType<ProjectTaskAssignmentItem> = [
    {
      title: 'Người',
      dataIndex: 'userAccountId',
      render: (userAccountId: string) => displayName(userAccountId),
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      width: 200,
      render: (role: ProjectTaskAssignmentRoleValue, assignment) =>
        canManage ? (
          <Select<ProjectTaskAssignmentRoleValue>
            size="small"
            value={role}
            style={{ width: 180 }}
            options={roleOptions}
            onChange={(value) => {
              void changeRole(assignment, value);
            }}
          />
        ) : (
          projectTaskAssignmentRoleNames[role]
        ),
    },
    {
      title: 'Giờ dự kiến',
      dataIndex: 'plannedEffortHours',
      width: 120,
      align: 'right',
      render: (hours: number | null) => hours ?? '—',
    },
    {
      title: 'Nhận việc',
      dataIndex: 'isAccepted',
      width: 160,
      render: (isAccepted: boolean, assignment) =>
        isAccepted ? (
          <Tag color="success">Đã nhận {formatDate(assignment.acceptedAt)}</Tag>
        ) : (
          <Tooltip title="Người này chưa mở phần mềm để xác nhận, hoặc chưa nhận việc. Giao lâu mà chưa xác nhận là dấu hiệu cần hỏi.">
            <Tag color="gold">Chưa xác nhận</Tag>
          </Tooltip>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, assignment) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={!canManage}
          onClick={() => {
            void remove(assignment.id);
          }}
        />
      ),
    },
  ];

  /*
    Chỉ hiện thành viên ĐANG tham gia dự án, và bỏ những người đã được giao việc này rồi. Người đã
    rời dự án vẫn đọc được dự án nhưng không nhận việc mới — đó chính là ý nghĩa của việc rời dự án.
  */
  const assignedIds = new Set(
    (assignments ?? []).map((assignment) => assignment.userAccountId),
  );

  const candidateOptions = members
    .filter((member) => member.isActive && !assignedIds.has(member.userAccountId))
    .map((member) => ({
      value: member.userAccountId,
      label: displayName(member.userAccountId),
    }));

  return (
    <Modal
      open={open}
      title={task === null ? 'Giao việc' : `Giao việc — ${task.wbsCode} ${task.name}`}
      width={860}
      footer={null}
      onCancel={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        {task?.isSummaryTask === true ? (
          <Alert
            type="info"
            showIcon
            message="Đây là một nhóm việc nên không giao người vào được"
            description="Nhóm việc là chỗ gom, không phải chỗ làm. Hãy giao vào từng việc con — nếu không, người nhận sẽ thấy một dòng việc mà không biết phải làm gì cụ thể."
          />
        ) : null}

        <Table<ProjectTaskAssignmentItem>
          rowKey="id"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={assignments as ProjectTaskAssignmentItem[] | undefined}
          loading={assignments === null && errorMessage === null}
        />

        {canManage && task?.isSummaryTask === false ? (
          <Form<AssignFormValues>
            form={form}
            layout="vertical"
            initialValues={{ role: ProjectTaskAssignmentRole.Owner }}
            onFinish={(values) => void submit(values)}
          >
            <Space wrap align="start" size="middle">
              <Form.Item
                name="userAccountId"
                label="Người"
                rules={[{ required: true, message: 'Chưa chọn người.' }]}
                extra="Chỉ hiện thành viên đang tham gia dự án."
                style={{ minWidth: 260 }}
              >
                <Select showSearch optionFilterProp="label" options={candidateOptions} />
              </Form.Item>
              <Form.Item name="role" label="Vai trò" style={{ minWidth: 190 }}>
                <Select<ProjectTaskAssignmentRoleValue> options={roleOptions} />
              </Form.Item>
              <Form.Item name="allocationPercent" label="Thời gian (%)">
                <InputNumber<number> min={1} max={100} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item name="plannedEffortHours" label="Giờ dự kiến">
                <InputNumber<number> min={0} max={2000} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={isSaving}
                >
                  Giao
                </Button>
              </Form.Item>
            </Space>
          </Form>
        ) : null}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Mỗi việc có đúng một người chịu trách nhiệm chính — việc có hai người chịu trách nhiệm
          chính là việc không ai chịu trách nhiệm.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
